// components/StrokeIntakeApp.js
import React, {useState} from 'react';

// 导入子组件
import BasicInfoForm from './forms/BasicInfoForm';
import OnsetInfoForm from './forms/OnsetInfoForm';
import SymptomsForm from './forms/SymptomsForm';
import MedicalHistoryForm from './forms/MedicalHistoryForm';
import PhysicalExamForm from './forms/PhysicalExamForm';
import DiagnosisPlanForm from './forms/DiagnosisPlanForm';
import MedicalRecordModal from './MedicalRecordModal';
import StepIndicator from './StepIndicator';

// 导入GPT记录生成工具
import {generateMedicalRecordWithGPT} from '../utils/gptApi';

// 导入记录生成工具
//import {generateMedicalRecord} from '../utils/recordGenerator';

function StrokeIntakeApp() {
    // 添加生成状态
    const [isGenerating, setIsGenerating] = useState(false);

    const [selectedModel, setSelectedModel] = useState('gpt4o'); // 默认使用GPT-4o

    // 当前步骤
    const [currentStep, setCurrentStep] = useState(0);

    // 生成的病程记录
    const [generatedRecord, setGeneratedRecord] = useState('');
    const [showRecord, setShowRecord] = useState(false);

    // 基础信息
    const [basicInfo, setBasicInfo] = useState({
        name: '',
        gender: '男',
        age: '',
        weight: '',
        admissionTime: new Date().toISOString().slice(0, 16),
        hospitalNumber: '',
        department: '神经内科急诊'
    });

    // 发病情况
    const [onsetInfo, setOnsetInfo] = useState({
        onsetTime: '',
        onsetState: '',
        witnesses: ''
    });

    // 主要症状
    const [symptoms, setSymptoms] = useState({
        firstSymptom: '',
        facialAsymmetry: false,
        limbWeakness: false,
        limbWeaknessSide: '右侧',
        speechIssues: false,
        visionProblems: false,
        dizziness: false,
        headache: false,
        headacheSeverity: '',
        onsetType: '突然发生',
        symptomProgression: ''
    });

    // 既往病史
    const [medicalHistory, setMedicalHistory] = useState({
        hypertension: false,
        hypertensionYears: '',
        hypertensionControl: '',
        diabetes: false,
        diabetesYears: '',
        diabetesControl: '',
        hyperlipidemia: false,
        atrialFibrillation: false,
        previousStroke: false,
        previousTIA: false,
        allergies: ''
    });

    // 体格检查
    const [physicalExam, setPhysicalExam] = useState({
        temperature: '36.7',
        heartRate: '82',
        respiratoryRate: '18',
        bloodPressure: '158/94',
        oxygenSaturation: '97',
        consciousness: '清醒',
        speech: '',
        muscleStrength: {
            rightUpper: '5',
            rightLower: '5',
            leftUpper: '5',
            leftLower: '5'
        },
        nihssScore: ''
    });

    // 初步诊断和治疗计划
    const [diagnosis, setDiagnosis] = useState('');
    const [treatmentPlan, setTreatmentPlan] = useState('');

    // 步骤标题
    const steps = [
        '基本信息',
        '发病情况',
        '主要症状',
        '既往病史',
        '体格检查',
        '诊断与计划'
    ];

    // 导航函数
    const nextStep = () => {
        setCurrentStep(prevStep => Math.min(prevStep + 1, steps.length - 1));
    };

    const prevStep = () => {
        setCurrentStep(prevStep => Math.max(prevStep - 1, 0));
    };

    // 处理生成病程记录
    // const handleGenerateRecord = () => {
    //     const record = generateMedicalRecord({
    //         basicInfo,
    //         onsetInfo,
    //         symptoms,
    //         medicalHistory,
    //         physicalExam,
    //         diagnosis,
    //         treatmentPlan
    //     });
    //
    //     setGeneratedRecord(record);
    //     setShowRecord(true);
    // };

    // 处理生成病程记录，修改为异步函数
    const handleGenerateRecord = async () => {
        try {
            setIsGenerating(true);

            // 导入统一的生成函数
            const { generateMedicalRecord } = await import('../services/aiService');

            // 调用GPT-4o API生成病程记录
            const record = await generateMedicalRecord(
                {
                    basicInfo,
                    onsetInfo,
                    symptoms,
                    medicalHistory,
                    physicalExam,
                    diagnosis,
                    treatmentPlan
                },
                selectedModel // 传递选择的模型
            );

            setGeneratedRecord(record);
            setShowRecord(true);
        } catch (error) {
            console.error("生成病程记录失败:", error);
            alert("生成病程记录时出错，请稍后重试。");
        } finally {
            setIsGenerating(false);
        }
    };

    // 渲染当前步骤的表单
    const renderCurrentStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <BasicInfoForm
                        data={basicInfo}
                        setData={setBasicInfo}
                    />
                );
            case 1:
                return (
                    <OnsetInfoForm
                        data={onsetInfo}
                        setData={setOnsetInfo}
                    />
                );
            case 2:
                return (
                    <SymptomsForm
                        data={symptoms}
                        setData={setSymptoms}
                    />
                );
            case 3:
                return (
                    <MedicalHistoryForm
                        data={medicalHistory}
                        setData={setMedicalHistory}
                    />
                );
            case 4:
                return (
                    <PhysicalExamForm
                        data={physicalExam}
                        setData={setPhysicalExam}
                    />
                );
            case 5:
                return (
                    <DiagnosisPlanForm
                        diagnosis={diagnosis}
                        setDiagnosis={setDiagnosis}
                        treatmentPlan={treatmentPlan}
                        setTreatmentPlan={setTreatmentPlan}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* 顶部标题栏 */}
            <header className="bg-blue-700 text-white py-4 px-6 shadow-md">
                <div className="max-w-5xl mx-auto flex justify-between items-center">
                    <h1 className="text-xl font-bold">脑卒中患者信息采集系统</h1>
                    <div className="text-sm">
                        当前时间: {new Date().toLocaleString('zh-CN')}
                    </div>
                </div>
            </header>

            {/* 主内容区 */}
            <main className="max-w-5xl mx-auto my-6 px-4">
                <div className="bg-white rounded-lg shadow-md p-6">
                    {/* 步骤指示器 */}
                    <StepIndicator
                        steps={steps}
                        currentStep={currentStep}
                    />

                    {/* 当前步骤的表单 */}
                    <div className="mt-6">
                        {renderCurrentStep()}
                    </div>

                    {/* 导航按钮 */}
                    <div className="mt-8 flex justify-between">
                        <button
                            onClick={prevStep}
                            disabled={currentStep === 0}
                            className={`px-4 py-2 rounded ${currentStep === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                        >
                            上一步
                        </button>

                        {currentStep < steps.length - 1 ? (
                            <button
                                onClick={nextStep}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                下一步
                            </button>
                        ) : (
                            <div className="flex space-x-2">
                                {/* 模型选择下拉框 */}
                                <select
                                    value={selectedModel}
                                    onChange={(e) => setSelectedModel(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={isGenerating}
                                >
                                    <option value="gpt4o">GPT-4o</option>
                                    <option value="deepseek-chat">DeepSeek V3</option>
                                </select>

                                {/* 生成按钮 */}
                                <button
                                    onClick={handleGenerateRecord}
                                    disabled={isGenerating}
                                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                                >
                                    {isGenerating ? (
                                        <span className="flex items-center">
                        <span className="w-2 h-2 bg-white rounded-full animate-bounce mr-1"></span>
                        <span className="w-2 h-2 bg-white rounded-full animate-bounce mr-1" style={{animationDelay: '0.2s'}}></span>
                        <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></span>
                        <span className="ml-2">生成中</span>
                    </span>
                                    ) : (
                                        "生成病程记录"
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* 病程记录模态框 */}
            {showRecord && (
                <MedicalRecordModal
                    record={generatedRecord}
                    onClose={() => setShowRecord(false)}
                />
            )}
        </div>
    );
}

export default StrokeIntakeApp;